import React, { useState } from "react";
import { useMsal } from "@azure/msal-react";
import axios from "axios";
import { COUNTRY_GROUP_MAP } from "../config/countryGroups";
import { API_BASE_URL, getApiToken } from "../config/authConfig";

const emptyEntry = () => ({ country: "", countryCode: "", securityGroupId: "", securityGroup: "", startDate: "", endDate: "" });

export default function TravelForm({ travelRequests, setTravelRequests, onReview, onViewHistory }) {
  const { accounts, instance } = useMsal();
  const user = accounts[0] || {};
  const isAdmin = user.username?.toLowerCase().startsWith("adm.");

  const [current, setCurrent] = useState(emptyEntry());
  const [errors, setErrors] = useState({});
  const [onBehalfOf, setOnBehalfOf] = useState(null);
  const [userSearch, setUserSearch] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);

  const validate = () => {
    const e = {};
    if (isAdmin && !onBehalfOf) e.behalf = "Please select a user to submit on behalf of.";
    if (!current.country) e.country = "Please select a country.";
    if (!current.startDate) e.startDate = "Please enter a start date.";
    if (!current.endDate) e.endDate = "Please enter an end date.";
    if (current.startDate && current.endDate && current.endDate < current.startDate)
      e.endDate = "End date must be after start date.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleUserSearch = async (e) => {
    const query = e.target.value;
    setUserSearch(query);
    if (query.length < 3) { setUserResults([]); return; }
    setSearchingUsers(true);
    try {
      const token = await getApiToken(instance);
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API_BASE_URL}/travel/searchUsers`, { params: { query }, headers });
      setUserResults(res.data.users || []);
    } catch (err) {
      console.error("User search failed:", err);
    } finally {
      setSearchingUsers(false);
    }
  };

  const handleSelectUser = (u) => {
    setOnBehalfOf(u);
    setUserSearch(u.displayName);
    setUserResults([]);
    setErrors(prev => ({ ...prev, behalf: "" }));
  };

  const handleCountryChange = (e) => {
    const selected = COUNTRY_GROUP_MAP.find((c) => c.code === e.target.value);
    setCurrent((prev) => ({
      ...prev,
      country: selected ? selected.name : "",
      countryCode: selected ? selected.code : "",
      securityGroupId: selected ? selected.securityGroupId : "",
      securityGroup: selected ? selected.securityGroup : "",
    }));
    setErrors((prev) => ({ ...prev, country: "" }));
  };

  const handleAddAnother = () => {
    if (!validate()) return;
    setTravelRequests((prev) => [...prev, current]);
    setCurrent(emptyEntry());
    setErrors({});
  };

  const handleReview = () => {
    if (!validate()) return;
    setTravelRequests((prev) => [...prev, current]);
    onReview(onBehalfOf);
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="page">
      <header className="app-header">
        <div className="header-inner">
          <div className="header-logo">
            <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
              <rect width="32" height="32" rx="8" fill="#230658" />
              <path d="M9 16L14 21L23 11" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>Travel Request Portal</span>
          </div>
          <div className="header-right">
            {isAdmin && <span className="admin-badge">Admin</span>}
            <button className="btn-nav" onClick={onViewHistory}>
              {isAdmin ? "All Requests" : "My Requests"}
            </button>
            <div className="header-user">
              <div className="user-avatar">{user.name ? user.name[0].toUpperCase() : "U"}</div>
              <span>{user.name}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="main-content">
        <div className="page-title-block">
          <h2>New Travel Request</h2>
          <p>Enter your travel destinations and dates. You can add multiple countries.</p>
        </div>

        <div className="form-card">
          {isAdmin && (
            <div className="form-group behalf-section">
              <label>Submit on behalf of</label>
              {!onBehalfOf ? (
                <div className="user-search-wrap">
                  <input
                    type="text"
                    placeholder="Search by name or email..."
                    value={userSearch}
                    onChange={handleUserSearch}
                    className={`user-search-input ${errors.behalf ? "error" : ""}`}
                  />
                  {searchingUsers && (
                    <div className="user-search-results">
                      <div style={{ padding: "12px 14px", color: "var(--text-muted)", fontSize: 13 }}>Searching...</div>
                    </div>
                  )}
                  {!searchingUsers && userResults.length > 0 && (
                    <div className="user-search-results">
                      {userResults.map((u) => (
                        <div key={u.id} className="user-search-item" onClick={() => handleSelectUser(u)}>
                          <div className="user-avatar sm">{u.displayName[0].toUpperCase()}</div>
                          <div>
                            <span className="admin-user-name">{u.displayName}</span>
                            <span className="admin-user-upn">{u.userPrincipalName}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="behalf-selected">
                  <div className="user-avatar sm">{onBehalfOf.displayName[0].toUpperCase()}</div>
                  <div style={{ flex: 1 }}>
                    <span className="admin-user-name">{onBehalfOf.displayName}</span>
                    <span className="admin-user-upn">{onBehalfOf.userPrincipalName}</span>
                  </div>
                  <button className="btn-remove" onClick={() => { setOnBehalfOf(null); setUserSearch(""); }}>✕</button>
                </div>
              )}
              {errors.behalf && <span className="error-msg">{errors.behalf}</span>}
            </div>
          )}

          <h3>{travelRequests.length === 0 ? "Where are you traveling?" : "Add another destination"}</h3>

          {travelRequests.length > 0 && (
            <div className="added-trips">
              <h3>Added destinations ({travelRequests.length})</h3>
              {travelRequests.map((req, i) => (
                <div key={i} className="trip-chip">
                  <div className="trip-chip-info">
                    <div className="trip-code-badge">{req.countryCode}</div>
                    <div>
                      <strong>{req.country}</strong>
                      <span className="trip-dates">{formatDate(req.startDate)} → {formatDate(req.endDate)}</span>
                    </div>
                  </div>
                  <button className="btn-remove" onClick={() => setTravelRequests(prev => prev.filter((_, idx) => idx !== i))}>✕</button>
                </div>
              ))}
            </div>
          )}

          <div className="form-group">
            <label>Country</label>
            <select value={current.countryCode || ""} onChange={handleCountryChange} className={errors.country ? "error" : ""}>
              <option value="">Select a country...</option>
              {COUNTRY_GROUP_MAP.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
            {errors.country && <span className="error-msg">{errors.country}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Travel start date <span className="tz-note">(EST)</span></label>
              <input type="date" min={today} value={current.startDate}
                onChange={(e) => { setCurrent(prev => ({ ...prev, startDate: e.target.value })); setErrors(prev => ({ ...prev, startDate: "" })); }}
                className={errors.startDate ? "error" : ""} />
              {errors.startDate && <span className="error-msg">{errors.startDate}</span>}
            </div>
            <div className="form-group">
              <label>Travel end date <span className="tz-note">(EST)</span></label>
              <input type="date" min={current.startDate || today} value={current.endDate}
                onChange={(e) => { setCurrent(prev => ({ ...prev, endDate: e.target.value })); setErrors(prev => ({ ...prev, endDate: "" })); }}
                className={errors.endDate ? "error" : ""} />
              {errors.endDate && <span className="error-msg">{errors.endDate}</span>}
            </div>
          </div>

          <div className="form-actions">
            <button className="btn-secondary" onClick={handleAddAnother}>+ Add another country</button>
            <button className="btn-primary" onClick={handleReview}>Review & Submit →</button>
          </div>
        </div>
      </main>
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const [y, m, d] = dateStr.split("-");
  return `${m}/${d}/${y}`;
}